/**
 * Durations are stored as whole minutes and always displayed as hh:mm.
 * The input accepts the shapes people actually type on a timesheet.
 */

export const MAX_MINUTES = 24 * 60;

const PATTERNS: { re: RegExp; toMinutes: (m: RegExpMatchArray) => number }[] = [
  // 1:30, 01:30, :45
  { re: /^(\d*):(\d{1,2})$/, toMinutes: (m) => Number(m[1] || 0) * 60 + Number(m[2]) },
  // 1h30, 1h30m, 1 h 30 m
  { re: /^(\d+)\s*h\s*(\d{1,2})\s*m?$/i, toMinutes: (m) => Number(m[1]) * 60 + Number(m[2]) },
  // 90m, 90 min
  { re: /^(\d+)\s*m(?:in(?:s|utes)?)?$/i, toMinutes: (m) => Number(m[1]) },
  // 2h, 1.5h
  { re: /^(\d+(?:\.\d+)?)\s*h(?:rs?|ours?)?$/i, toMinutes: (m) => Number(m[1]) * 60 },
  // bare number: 2 => 2 hours, 1.5 => 90 minutes
  { re: /^(\d+(?:\.\d+)?)$/, toMinutes: (m) => Number(m[1]) * 60 },
];

/**
 * Parses a typed duration into minutes, or returns null when it is not a
 * duration we can trust. Rejects zero, anything past 24:00, and hh:mm forms
 * whose minute part has overflowed (1:75).
 */
export function parseDuration(input: string): number | null {
  const text = input.trim();
  if (!text) return null;

  for (const { re, toMinutes } of PATTERNS) {
    const match = text.match(re);
    if (!match) continue;

    // In a colon or h-and-m form the minute part must be a real minute value.
    if ((re.source.includes(':') || /h/.test(re.source)) && match[2] !== undefined) {
      if (Number(match[2]) > 59) return null;
    }

    const minutes = Math.round(toMinutes(match));
    if (!Number.isFinite(minutes) || minutes <= 0 || minutes > MAX_MINUTES) return null;
    return minutes;
  }
  return null;
}

/** Renders minutes as hh:mm. Totals may exceed 24 hours, so hours are not clamped. */
export function formatDuration(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Decimal hours, for spreadsheet columns where a number beats a string. */
export function toDecimalHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100;
}
